import { useCallback, useContext, useState } from "react";
import { notBlank } from "../util/ext";
import {
  useAuthenticationCreate,
  useAuthenticationDelete,
} from "./authenticationService";
import { lensProp, set } from "ramda";
import { AlertContext } from "../components/alert/AlertProvider";

const useAuthenticationsHandler = (
  { initialDialogState, callbacks = {}, organizationId } = {
    initialDialogState: {},
    callbacks: {},
  },
) => {
  const { alertError } = useContext(AlertContext);

  const [currentAuthentication, setCurrentAuthentication] = useState(null);
  const [authDialogs, setAuthDialogs] = useState(initialDialogState);

  const authenticationDelete = useAuthenticationDelete();
  const handleAuthenticationDeleteClick = async authentication => {
    try {
      await authenticationDelete.mutateAsync({
        resourceIds: {
          organizationId: authentication.organizationId,
          type: authentication.type,
          id: authentication.authenticationId,
        },
      });
      if (notBlank(callbacks.afterDelete)) {
        await callbacks.afterDelete(authentication);
      }
    } catch (err) {
      alertError(err);
    }
  };

  const scopeAuthenticationCreate = useAuthenticationCreate();
  const handleAuthenticationMenuSelect = useCallback(
    async type => {
      if (type === "scope" && notBlank(organizationId)) {
        await scopeAuthenticationCreate.mutateAsync({
          resourceIds: {
            organizationId: organizationId,
            type: "scope",
          },
        });
        if (notBlank(callbacks.afterSave)) {
          await callbacks.afterSave();
        }
      } else {
        setAuthDialogs(set(lensProp(type), true, initialDialogState));
      }
    },
    [organizationId, callbacks, initialDialogState, scopeAuthenticationCreate],
  );

  const handleAuthenticationEditClick = useCallback(
    async authentication => {
      setCurrentAuthentication(authentication);
      await handleAuthenticationMenuSelect(authentication.type);
    },
    [handleAuthenticationMenuSelect],
  );

  const handleAuthenticationDialogClose = () => {
    setAuthDialogs(initialDialogState);
    setCurrentAuthentication(null);
  };

  const handleAuthenticationDialogSave = async authentication => {
    setAuthDialogs(initialDialogState);
    setCurrentAuthentication(null);
    if (notBlank(callbacks.afterSave)) {
      await callbacks.afterSave(authentication);
    }
  };

  return {
    authDialogs,
    currentAuthentication,
    handleAuthenticationDeleteClick,
    handleAuthenticationMenuSelect,
    handleAuthenticationEditClick,
    handleAuthenticationDialogClose,
    handleAuthenticationDialogSave,
  };
};

export { useAuthenticationsHandler };
